'use client';

import { useMemo } from 'react';
import { Star, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import FinalCTASection from '@/components/treatments/shared/final-cta-section';

interface Testimonial {
  id: string;
  name: string;
  rating: number;
  testimonial: string;
  location: string;
  treatmentType: string;
}

export default function TestimonialsPage() {
  const { t, isLoading } = useTranslation('testimonials');
  const { isRTL } = useRTL();

  // Get testimonials from translations with runtime validation
  const testimonials = useMemo((): Testimonial[] => {
    if (isLoading) return [];
    const raw = t('testimonials', { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((item): item is Record<string, unknown> =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as Record<string, unknown>).id === 'string' &&
        typeof (item as Record<string, unknown>).name === 'string' &&
        typeof (item as Record<string, unknown>).testimonial === 'string' &&
        typeof (item as Record<string, unknown>).location === 'string' &&
        typeof (item as Record<string, unknown>).treatmentType === 'string' &&
        (typeof (item as Record<string, unknown>).rating === 'number' || typeof (item as Record<string, unknown>).rating === 'string')
      )
      .map((item): Testimonial => ({
        id: item.id as string,
        name: item.name as string,
        rating: Number(item.rating) || 0,
        testimonial: item.testimonial as string,
        location: item.location as string,
        treatmentType: item.treatmentType as string,
      }));
  }, [isLoading, t]);

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1C2E4A] via-[#122237] to-[#0A1D44]">
        <div className="absolute inset-0 bg-[url('/images/patterns/circuit-pattern.svg')] opacity-5" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`text-center max-w-4xl mx-auto ${isRTL ? 'text-right' : ''}`}
          >
            {isLoading ? (
              <div className="animate-pulse">
                <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-2xl mx-auto"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-8 max-w-3xl mx-auto"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="text-center">
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-24 mx-auto"></div>
                      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32 mx-auto"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-serif">
                  {t('hero.title')}
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  {t('hero.subtitle')}
                </p>
                
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gold mb-2">{t('hero.stats.patients.number')}</div>
                    <div className="text-gray-300">{t('hero.stats.patients.label')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gold mb-2">{t('hero.stats.successRate.number')}</div>
                    <div className="text-gray-300">{t('hero.stats.successRate.label')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gold mb-2">{t('hero.stats.rating.number')}</div>
                    <div className="text-gray-300">{t('hero.stats.rating.label')}</div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-[#F8FAFC] dark:bg-[#0F172A]">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 animate-pulse">
                  <div className="flex mb-4 space-x-1">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <div key={j} className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    ))}
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  </div>
                  <div className="flex items-center border-t pt-6">
                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full mr-4"></div>
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-[#1A2539] rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300 border border-[#E2E8F0] dark:border-[#2D3748]"
                >
                  {/* Rating */}
                  <div className={`flex mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {Array.from({ length: Math.min(5, Math.max(0, Math.floor(testimonial.rating))) }).map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                    ))}
                  </div>

                  {/* Content */}
                  <p className={`text-[#4A5568] dark:text-[#CBD5E0] mb-6 italic leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                    "{testimonial.testimonial}"
                  </p>

                  {/* Author Info */}
                  <div className={`flex items-center border-t border-[#E2E8F0] dark:border-[#2D3748] pt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`relative w-16 h-16 ${isRTL ? 'ml-4' : 'mr-4'}`}>
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-gold-600 flex items-center justify-center text-white font-semibold text-xl">
                        {testimonial.name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <h4 className="font-semibold text-[#0A1D44] dark:text-white mb-1">
                        {testimonial.name}
                      </h4>
                      <div className={`flex items-center text-sm text-[#4A5568] dark:text-[#CBD5E0] mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <MapPin className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        {testimonial.location}
                      </div>
                      <div className="text-sm text-gold font-medium">
                        {testimonial.treatmentType}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {!isLoading && (
        <FinalCTASection 
          treatmentName={t('cta.treatmentName')}
          treatmentKeyword="dental treatments"
          scrollToSection="booking"
          customHeading={
            <>
              {t('cta.heading.part1')} <span className="text-gold">{t('cta.heading.part2')}</span>
            </>
          }
          customSubheading={t('cta.subheading')}
        />
      )}
    </main>
  );
}